// TransportV2Context - Zentraler State-Container fuer die V2-Page.
// Der Reducer bildet exakt das state-Shape aus dem Mock ab, ergaenzt um
// Live-Daten (instances, packages, precheckByBucket, globalRefs, orphans,
// artifactConfigs, pipelineRun, releaseCandidate).
//
// Wichtige Konventionen:
//   - state.sel[sourceId][werkKey] = true   (Werk-Auswahl, Key = Source-Id)
//   - state.cfg[werkKey][sourceId][paramKey] = normalizedConfigEntry
//   - state.precheckByBucket[bucketKey] = { comparison, orphans, references, error }
//     bucketKey = `${targetInstanceId}__${werkKennung||"std"}`
//   - state.pipeline: idle | snapshotting | dispatching | running | done | pending | failed | released
//   - suffixedId wird NIE rekonstruiert, immer aus precheck durchgereicht.

import React, { createContext, useContext, useMemo, useReducer } from "react";

const initialState = {
  step: 0,
  spaceKey: null,
  spaceId: null,
  space: null,
  sourceInstanceId: null,
  targetInstanceIds: [],
  werkOn: {},
  pkgId: null,
  pkgName: null,
  pkgVersion: null,
  pkgMod: null,
  precheckByBucket: {},
  globalRefs: [],
  refSel: {},
  artifactDeselection: {},
  globalRefDeselection: {},
  orphanSel: {},
  expanded: {},
  sel: {},
  deploy: {},
  refDeploy: {},
  cfg: {},
  // Parameter values maintained in Git, preloaded per kennung and source artifact:
  //   { "<kennung>" | "": { "<sourceArtifactId>": { "<paramKey>": { DataType, Description,
  //                                                                 values: { __base__, <stage> } } } } }
  // Git is the truth for the parameters. Without this preload the wizard showed only the tenants'
  // current values and the snapshot discarded everything it did not know - a QAS transport thus
  // deleted the maintained PROD values.
  gitCfg: {},
  // idle/laedt hold the configuration view, da/leer/fehler let it continue.
  // "leer" = no space or no package, so nothing to preload.
  gitCfgState: "idle",
  gitCfgUnvollstaendig: false,
  cfgTab: null,
  workingTab: "all",
  workingTabAuto: true,
  scheduleCount: null,
  pipeline: "idle",
  pipelineError: null,
  runInfo: null,
  execLogs: [],
  pdReplicate: true,
  pdSummary: { pids: 0, chained: 0 },
  // Ausgewaehlte Partner-Directory-Kennungen (Pids), die mit-transportiert werden.
  // Setzt sich aus automatisch erkannten (aus BPMN + ProcessDirect-Reverse-Match)
  // und manuell hinzugefuegten Pids zusammen. Bei pdReplicate=false wird die
  // Liste ignoriert. Dedupliziert.
  pdSelectedPids: [],
  // Etappe C: Stufen-Filter je Pid. { [pid]: string[] } mit Stufen-Keys
  // ("qas", "preprod", "prod" - jeweils lowercase ohne Bindestrich, wie der Backend-
  // Normalisierer normStage erwartet). Fehlt der Eintrag fuer einen Pid, gelten
  // Backend-seitig alle Stufen ("*") - identisch zum Altverhalten. Wird beim Paket-
  // wechsel wie pdSelectedPids zurueckgesetzt.
  pdStagesByPid: {},
  releaseState: "idle",
  releaseCandidate: null,
  releaseError: null,
  finalized: false,
};

// Nur-Keys-Variante von vorwaertsStufen(state) - der Reducer darf keine
// selector-Objekte konstruieren, braucht aber die Vorwaerts-Stufen-Keys fuer die
// PD-Vorbelegung. Bewusst dieselbe Regel wie vorwaertsStufen (Ziel-Stufe bis
// PROD, nur Stufen mit erreichbarer Instanz).
function vorwaertsStufenKeysAus(space) {
  if (!space || !Array.isArray(space.stages) || !space.zielStufe) return [];
  const startIdx = space.stages.findIndex((st) => st.stageKey === space.zielStufe);
  if (startIdx < 0) return [];
  const out = [];
  for (let i = startIdx; i < space.stages.length; i++) {
    const st = space.stages[i];
    if (!st || !st.instanceId || !st.stageKey) continue;
    out.push(st.stageKey);
  }
  return out;
}

// Vorbelegung: fuer alle Pids in list, die noch KEINEN Eintrag in stagesMap haben,
// den Default-Satz aller Vorwaerts-Stufen setzen. Bestehende (bewusst reduzierte)
// Auswahl wird NICHT ueberschrieben - sonst zerreisst jede erneute Response die
// Nutzerintention.
function pdStagesVorbelegen(stagesMap, list, defaults) {
  if (!Array.isArray(list) || !list.length) return stagesMap;
  if (!Array.isArray(defaults) || !defaults.length) return stagesMap;
  let out = null;
  for (const pid of list) {
    if (typeof pid !== "string" || !pid) continue;
    if (Array.isArray(stagesMap?.[pid])) continue;
    if (!out) out = { ...(stagesMap || {}) };
    out[pid] = [...defaults];
  }
  return out || stagesMap || {};
}

// Referenzen folgen den iFlows. Eine globale Referenz (Message Mapping, Script Collection,
// Value Mapping) reist nur mit, wenn mindestens einer der iFlows, die sie nutzen, selbst im
// Transport ist. Ein Value Mapping ohne iFlow-Verweis (herkunft "paket") hat keine Nutzer und
// faellt unter "ohne bekannte Nutzer bleibt das bisherige Verhalten".
// Die Nutzer liefert package-references als referencedBy (Quell-Artefakt-Ids). Faellt der
// letzte nutzende iFlow raus - abgewaehlt, identisch, Entwurf -, faellt die Referenz mit;
// kommt er wieder dazu, greift die Vorbelegung nach Status. Ohne bekannte Nutzer bleibt
// das bisherige Verhalten. Eine bewusste Abwahl (refSel === false) ueberlebt, ein
// "nicht gebraucht" wird als fehlender Eintrag abgelegt, damit beides unterscheidbar bleibt.
const refKey = (r) => r?.bundleId || r?.id || r?.Id || r?.name || r?.Name;
const refIstEntwurf = (r) => r?.draft === true || String(r?.status || "").toLowerCase() === "draft" || String(r?.version || r?.Version || "").toLowerCase() === "draft";
// v10: package-references liefert den Referenztyp autoritativ aus dem iFlow-MANIFEST und meldet
// Referenzen, die sich per Schnittstelle nicht transportieren lassen (Function Libraries u. a.,
// transportierbar=false). Die bleiben sichtbar, kommen aber NIE in die Auswahl.
const refNichtTransportierbar = (r) => r?.transportierbar === false;
// Ein Value Mapping, das im Ziel schon existiert, ueberschreibt dort die gepflegten Werte
// (ueberschreibtZielwerte=true). Das darf keine Vorbelegung entscheiden - der Nutzer hakt es
// bewusst an. "Nicht vorbelegt" ist der fehlende Eintrag (undefined), nicht false: false ist
// die bewusste Abwahl, und die Referenz-folgt-iFlow-Ableitung unterscheidet beides.
const refUeberschreibtZiel = (r) => r?.ueberschreibtZielwerte === true;
export function gewaehlteIflows(sel) {
  const out = new Set();
  for (const [src, werke] of Object.entries(sel || {})) {
    if (Object.values(werke || {}).some(Boolean)) out.add(String(src));
  }
  return out;
}
export function refNutzer(r) {
  return Array.isArray(r?.referencedBy) ? r.referencedBy.map((x) => String(x || "")).filter(Boolean) : [];
}
export function refGebraucht(r, gewaehlt) {
  const nutzer = refNutzer(r);
  return nutzer.length ? nutzer.some((id) => gewaehlt.has(id)) : true;
}
function refSelNachIflows(refSel, globalRefs, sel) {
  const gewaehlt = gewaehlteIflows(sel);
  const out = { ...(refSel || {}) };
  for (const r of globalRefs || []) {
    const id = refKey(r);
    if (!id || !refNutzer(r).length) continue;
    if (r?.resolvable === false || refIstEntwurf(r) || refNichtTransportierbar(r)) continue;
    if (!refGebraucht(r, gewaehlt)) { delete out[id]; continue; }
    if (out[id] === undefined) {
      const vb = refVorbelegung(r);
      if (vb !== undefined) out[id] = vb;
    }
  }
  return out;
}
// Vorbelegung nach Status: identisch -> bewusst aus (false), sonst an (true). Ein Value Mapping,
// das Zielwerte ueberschreiben wuerde, bleibt ohne Eintrag (undefined) - der Nutzer entscheidet.
function refVorbelegung(r) {
  if (String(r?.status || "").toLowerCase() === "identical") return false;
  if (refUeberschreibtZiel(r)) return undefined;
  return true;
}

export function reducer(state, action) {
  switch (action.type) {
    case "SET_STEP": {
      let ziel = action.payload;
      // Ohne gepflegte Werke ist Schritt 1 (Landschaften) nur eine Wiederholung der Space-Karte:
      // er wird uebersprungen - vorwaerts nach 2, rueckwaerts nach 0. Zentral hier, damit jeder
      // Absender (Weiter, Zurueck, Stepper-Klick) dieselbe Regel bekommt.
      if (ziel === 1 && !landschaftsSchrittNoetig(state)) ziel = (state.step || 0) < 1 ? 2 : 0;
      return { ...state, step: ziel };
    }
    case "PICK_SPACE": {
      const { space, sourceInstance, qasInstance, prodInstance, werkMap, spaceKey } = action.payload;
      const changed = state.spaceKey !== spaceKey;
      if (!changed) return state;
      const werkOn = {};
      const werkeList = qasInstance ? extractWerkeFromInstance(qasInstance) : [];
      for (const w of werkeList) werkOn[w.key] = true;
      return {
        ...initialState,
        scheduleCount: state.scheduleCount,
        spaceKey,
        spaceId: space?.id || null,
        space: { ...space, sourceInstance, qasInstance, prodInstance, werkeList },  // space traegt stufen/quellStufe/zielStufe aus Scene0
        sourceInstanceId: sourceInstance?.id || null,
        targetInstanceIds: qasInstance ? [qasInstance.id] : [],
        werkOn,
      };
    }
    case "TOGGLE_WERK": {
      const key = action.payload;
      const next = { ...state.werkOn, [key]: !state.werkOn[key] };
      // gitCfg depends on the plant selection: a newly enabled plant has its own file in Git that
      // is not loaded yet. Keeping the old state would leave the new plant without its maintained
      // values and overwrite them on the next snapshot.
      return { ...state, werkOn: next, precheckByBucket: {}, sel: {}, deploy: {}, refDeploy: {}, cfg: {}, cfgTab: null, gitCfg: {}, gitCfgState: "idle" };
    }
    case "PICK_PACKAGE": {
      const { id, name, version, mod } = action.payload;
      const changed = state.pkgId !== id;
      if (!changed) return state;
      return {
        ...state,
        pkgId: id,
        pkgName: name || id,
        pkgVersion: version || null,
        pkgMod: mod || null,
        precheckByBucket: {},
        globalRefs: [],
        refSel: {},
        sel: {},
        deploy: {},
        refDeploy: {},
        cfg: {},
        gitCfg: {},
        gitCfgState: "idle",
        cfgTab: null,
        workingTab: "all",
        artifactDeselection: {},
        globalRefDeselection: {},
        orphanSel: {},
        // Erkannte Pids gehoeren zum konkreten Paket. Wenn das Paket wechselt,
        // muss die Auswahl neu aufgebaut werden - sonst wandern PD-Kennungen
        // ins falsche Paket.
        pdSelectedPids: [],
        // Stufen-Filter je Pid gehoert genauso zum Paket - alter Zustand darf nicht
        // die Vorbelegung im neuen Paket ueberschreiben.
        pdStagesByPid: {},
      };
    }
    case "SET_PRECHECK":
      return { ...state, precheckByBucket: action.payload || {} };
    case "SET_GLOBAL_REFS": {
      const list = Array.isArray(action.payload) ? action.payload : [];
      // Schluessel der NEUEN Liste (Id vor Name, wie unten). Auswahl-Eintraege, die hier nicht
      // mehr vorkommen, fallen raus: ein Reload kann eine Referenz entfernen, die alte Auswahl
      // haette sie weiter mitgeschickt - ohne Treffer in globalRefs ginge sie mit leerem Typ
      // in den Snapshot und der bricht mit 404 ab.
      const gueltig = new Set();
      for (const r of list) {
        const key = r?.bundleId || r?.id || r?.Id || r?.name || r?.Name;
        if (key) gueltig.add(key);
      }
      const refSel = {};
      for (const [k, v] of Object.entries(state.refSel || {})) {
        if (gueltig.has(k)) refSel[k] = v;
      }
      for (const r of list) {
        // Id VOR Name (der Snapshot laedt per Id) - identische Ableitung wie in Scene3/Scene5,
        // sonst laufen die refSel-Schluessel zwischen Vorauswahl und Anzeige auseinander.
        const id = r?.bundleId || r?.id || r?.Id || r?.name || r?.Name;
        if (!id) continue;
        // Nicht aufloesbare Referenz (Backend: resolvable=false) darf NIE in die
        // Transport-Auswahl geraten. Wir setzen die Auswahl hart auf false und
        // ueberschreiben eine ggf. schon existierende Vorbelegung - der Anwender
        // koennte diese Referenz nie mit einem sinnvollen Ergebnis mit-transportieren.
        if (r?.resolvable === false) {
          refSel[id] = false;
          continue;
        }
        // Entwuerfe genauso hart ausschliessen: ohne aktive Version kann der Snapshot
        // die Referenz nicht laden und wuerde den ganzen Transport abbrechen.
        const status = String(r?.status || "").toLowerCase();
        const draft = r?.draft === true || status === "draft" || String(r?.version || r?.Version || "").toLowerCase() === "draft";
        if (draft) {
          refSel[id] = false;
          continue;
        }
        // Per Schnittstelle nicht transportierbar (Function Library u. a.): wird gemeldet,
        // aber nie transportiert - Auswahl hart aus, auch gegen eine alte Vorbelegung.
        if (refNichtTransportierbar(r)) {
          refSel[id] = false;
          continue;
        }
        if (refSel[id] === undefined) {
          // Ein Value Mapping mit ueberschreibtZielwerte bleibt hier ohne Eintrag: nicht
          // vorbelegt, damit der Nutzer die Freigabe bewusst je Zeile trifft.
          const vb = refVorbelegung(r);
          if (vb !== undefined) refSel[id] = vb;
        }
      }
      return { ...state, globalRefs: list, refSel: refSelNachIflows(refSel, list, state.sel) };
    }
    case "SET_SEL": {
      const sel = action.payload || {};
      return { ...state, sel, refSel: refSelNachIflows(state.refSel, state.globalRefs, sel) };
    }
    case "TOGGLE_SEL": {
      const { sourceId, werkKey, on } = action.payload;
      const cur = { ...(state.sel[sourceId] || {}) };
      if (on) cur[werkKey] = true;
      else delete cur[werkKey];
      const next = { ...state.sel, [sourceId]: cur };
      return { ...state, sel: next, refSel: refSelNachIflows(state.refSel, state.globalRefs, next) };
    }
    case "SET_DEPLOY": {
      const { sourceId, werkKey, on } = action.payload || {};
      if (!state.sel?.[sourceId]?.[werkKey] || typeof on !== "boolean") return state;
      return { ...state, deploy: { ...state.deploy, [sourceId]: { ...state.deploy?.[sourceId], [werkKey]: on } } };
    }
    case "SET_REF_DEPLOY": {
      const { id, on } = action.payload || {};
      const ref = (state.globalRefs || []).find(r => refKey(r) === id);
      if (!state.refSel?.[id] || !ref || typeof on !== "boolean" || (!on && referenceDeployRequired(state, ref))) return state;
      return { ...state, refDeploy: { ...state.refDeploy, [id]: on } };
    }
    case "TOGGLE_REF": {
      const id = action.payload;
      // Nicht-aufloesbare Referenz (resolvable=false) darf NIE in die Auswahl geraten. Die Checkbox
      // ist zwar disabled, aber ein programmatischer Dispatch soll ebenfalls abprallen (Defense-in-depth).
      const ref = (state.globalRefs || []).find((r) => (r?.bundleId || r?.id || r?.Id || r?.name || r?.Name) === id);
      if (ref?.resolvable === false) return state;
      // Entwuerfe prallen genauso ab wie beim Vorbelegen (SET_GLOBAL_REFS): ohne aktive
      // Version kann der Snapshot sie nicht laden und der Transport braeche ab.
      const refDraft = ref?.draft === true || String(ref?.status || "").toLowerCase() === "draft" || String(ref?.version || ref?.Version || "").toLowerCase() === "draft";
      if (refDraft) return state;
      // Nicht transportierbare Referenz (transportierbar=false) prallt genauso ab: sie wird nur
      // gemeldet, ein Haken haette keinen Transport zur Folge und wuerde den Snapshot verwirren.
      if (refNichtTransportierbar(ref)) return state;
      // Referenzen folgen den iFlows: ohne einen nutzenden iFlow im Transport laesst sich eine
      // Referenz nicht anwaehlen (die Checkbox ist gesperrt, der Dispatch prallt ebenfalls ab).
      if (refNutzer(ref).length && !refGebraucht(ref, gewaehlteIflows(state.sel))) return state;
      return { ...state, refSel: { ...state.refSel, [id]: !state.refSel[id] } };
    }
    case "TOGGLE_EXPAND": {
      const id = action.payload;
      return { ...state, expanded: { ...state.expanded, [id]: !state.expanded[id] } };
    }
    case "SET_EXPANDED":
      return { ...state, expanded: action.payload || {} };
    case "SET_WORKING_TAB":
      return { ...state, workingTab: action.payload, workingTabAuto: false };
    case "SET_WORKING_TAB_AUTO":
      return { ...state, workingTab: action.payload, workingTabAuto: true };
    case "SET_CFG": {
      // stageValues (optional, Etappe B): { [stageKey]: [{ParameterKey, targetValue}] }.
      // Wird pro Eintrag in entries[i].byStage[stageKey] zusammengefuehrt. Der Root des
      // Eintrags bleibt die BASIS (erste Vorwaerts-Stufe) - Rueckwaerts-kompatibel zur
      // Ein-Stufen-Ansicht.
      const { werkKey, sourceId, cacheKey, entries, stageValues } = action.payload;
      const stufen = stageValues && typeof stageValues === "object" ? stageValues : null;
      // Pro Stufe ein Lookup ParameterKey -> targetValue (fuer schnellen Zugriff im map).
      const stageLookup = {};
      if (stufen) {
        for (const [stageKey, list] of Object.entries(stufen)) {
          const m = {};
          for (const v of Array.isArray(list) ? list : []) {
            if (v && v.ParameterKey != null) m[v.ParameterKey] = v.targetValue;
          }
          stageLookup[stageKey] = m;
        }
      }
      // Values this plant has maintained in Git. They BEAT the tenant's current value: Git is the
      // truth for the parameters, the tenant only shows what happens to be there. Without that
      // precedence the user would see the tenant value on every transport, adopt it, and the
      // state maintained in Git would be silently overwritten.
      const gitWerk = (state.gitCfg && state.gitCfg[werkKey]) || {};
      const gitArt = gitWerk[sourceId] || {};
      const enrichedEntries = (entries || []).map((e) => {
        if (!e) return e;
        const git = gitArt[e.ParameterKey];
        const gitBasis = git?.values?.__base__;
        // fromTarget has to be reset as well: if the Git value happens to match the tenant value,
        // the row would otherwise read "from target" although the value comes from Git. byStage
        // below already handles this correctly.
        const root = gitBasis != null && gitBasis !== ""
          ? { ...e, newValue: gitBasis, fromGit: true, fromTarget: false }
          : e;
        if (!stufen) return root;
        const byStage = { ...(root.byStage || {}) };
        for (const stageKey of Object.keys(stageLookup)) {
          const targetValue = stageLookup[stageKey][e.ParameterKey];
          const gitWert = git?.values?.[stageKey];
          const ausGit = gitWert != null && gitWert !== "";
          byStage[stageKey] = {
            targetValue: targetValue == null ? "" : targetValue,
            newValue: ausGit ? gitWert : (targetValue || ""),
            fromTarget: !ausGit && !!targetValue,
            fromGit: ausGit,
            modified: false,
          };
        }
        return { ...root, byStage };
      });
      const werkCfg = { ...(state.cfg[werkKey] || {}) };
      werkCfg[sourceId] = { cacheKey, entries: enrichedEntries };
      return { ...state, cfg: { ...state.cfg, [werkKey]: werkCfg } };
    }
    case "SET_GIT_CFG": {
      // Preloaded Git values. The key is the UI's plant key ("std" = no plant), while the backend
      // sends the kennung ("" = no plant) - rekeyed once here so the rest of the view only ever
      // deals with one spelling.
      const { werke, unvollstaendig } = action.payload || {};
      const gitCfg = {};
      for (const [kennung, artefakte] of Object.entries(werke || {})) {
        gitCfg[kennung === "" ? "std" : kennung] = artefakte || {};
      }
      return { ...state, gitCfg, gitCfgState: "da", gitCfgUnvollstaendig: !!unvollstaendig };
    }
    case "SET_GIT_CFG_STATE":
      return { ...state, gitCfgState: action.payload };
    case "PATCH_CFG_ENTRY": {
      // Optional payload.stage: schreibt in entries[i].byStage[stage], sonst in den Root (Basis).
      const { werkKey, sourceId, paramKey, patch, stage } = action.payload;
      const werk = state.cfg[werkKey] || {};
      const artef = werk[sourceId] || { entries: [] };
      const entries = (artef.entries || []).map((e) => {
        if (e.ParameterKey !== paramKey) return e;
        if (!stage) return { ...e, ...patch };
        const byStage = { ...(e.byStage || {}) };
        byStage[stage] = { ...(byStage[stage] || {}), ...patch };
        return { ...e, byStage };
      });
      const nextArtef = { ...artef, entries };
      return {
        ...state,
        cfg: {
          ...state.cfg,
          [werkKey]: { ...werk, [sourceId]: nextArtef },
        },
      };
    }
    case "SET_CFG_TAB":
      return { ...state, cfgTab: action.payload };
    case "SET_SCHEDULE_COUNT":
      return { ...state, scheduleCount: action.payload };
    case "SET_PIPELINE_STATE":
      return {
        ...state,
        pipeline: action.payload.pipeline,
        pipelineError: action.payload.error ?? state.pipelineError,
        runInfo: action.payload.runInfo ?? state.runInfo,
      };
    case "APPEND_LOG":
      return { ...state, execLogs: [...state.execLogs, action.payload] };
    case "RESET_LOGS":
      return { ...state, execLogs: [] };
    case "SET_PD_REPLICATE": {
      // Abwaehlen leert die Auswahl (nichts replizieren), statt den Bereich zu verstecken - die
      // Liste bleibt sichtbar und bedienbar, wie die Kopf-Checkboxen der vorherigen Schritte.
      if (!action.payload) return { ...state, pdReplicate: false, pdSelectedPids: [], pdStagesByPid: {} };
      return { ...state, pdReplicate: true };
    }
    case "SET_PD_SUMMARY":
      return { ...state, pdSummary: action.payload || { pids: 0, chained: 0 } };
    case "SET_PD_SELECTED_PIDS": {
      const list = Array.isArray(action.payload) ? action.payload : [];
      const dedup = Array.from(new Set(list.filter((p) => typeof p === "string" && p)));
      // Neue Pids mit allen Vorwaerts-Stufen vorbelegen; bereits vorhandene bleiben.
      // So verhaelt sich ein Pid ohne bewusste Einschraenkung genau wie heute: alle Stufen.
      const defaults = vorwaertsStufenKeysAus(state.space);
      const stagesNext = pdStagesVorbelegen(state.pdStagesByPid || {}, dedup, defaults);
      // Aufraeumen: Stufen-Eintraege abgewaehlter Pids fallen mit raus. Sonst wuerde ein
      // Abwaehlen + spaeteres Wiederauswaehlen den alten (moeglicherweise reduzierten)
      // Filter zurueckholen, den der Nutzer nicht mehr im Kopf hat.
      const selSet = new Set(dedup);
      const cleaned = {};
      for (const [pid, stg] of Object.entries(stagesNext || {})) {
        if (selSet.has(pid)) cleaned[pid] = stg;
      }
      // Das Flag folgt der Auswahl: eine Pid gewaehlt heisst replizieren, keine heisst nichts.
      return { ...state, pdSelectedPids: dedup, pdStagesByPid: cleaned, pdReplicate: dedup.length > 0 };
    }
    case "TOGGLE_PD_PID": {
      const pid = action.payload;
      if (!pid || typeof pid !== "string") return state;
      const cur = new Set(state.pdSelectedPids || []);
      const stagesNext = { ...(state.pdStagesByPid || {}) };
      if (cur.has(pid)) {
        cur.delete(pid);
        // Filter mit raus, damit ein spaeteres Wieder-Anwaehlen sauber vorbelegt startet.
        delete stagesNext[pid];
      } else {
        cur.add(pid);
        // Vorbelegung nur setzen, wenn noch kein Eintrag existiert.
        if (!Array.isArray(stagesNext[pid])) {
          const defaults = vorwaertsStufenKeysAus(state.space);
          if (defaults.length) stagesNext[pid] = [...defaults];
        }
      }
      return { ...state, pdSelectedPids: Array.from(cur), pdStagesByPid: stagesNext, pdReplicate: cur.size > 0 };
    }
    case "TOGGLE_PD_STAGE": {
      // Einzelne Stufe fuer eine Pid an/aus. Wird beim Abwaehlen der letzten Stufe
      // der Pid komplett aus der Auswahl entfernt - sonst bliebe ein "Pid ausgewaehlt,
      // aber fuer keine Stufe" widerspruechlich in der Oberflaeche stehen.
      const { pid, stage } = action.payload || {};
      if (!pid || !stage) return state;
      const cur = new Set(state.pdStagesByPid?.[pid] || []);
      if (cur.has(stage)) cur.delete(stage); else cur.add(stage);
      const naechsteStufen = Array.from(cur);
      if (naechsteStufen.length === 0) {
        // Letzte Stufe entfernt -> Pid faellt raus. Konsistent zum Verhalten
        // "keine Stufe = nirgends transportieren".
        const selNext = (state.pdSelectedPids || []).filter((p) => p !== pid);
        const stagesNext = { ...(state.pdStagesByPid || {}) };
        delete stagesNext[pid];
        return { ...state, pdSelectedPids: selNext, pdStagesByPid: stagesNext, pdReplicate: selNext.length > 0 };
      }
      return {
        ...state,
        pdStagesByPid: { ...(state.pdStagesByPid || {}), [pid]: naechsteStufen },
      };
    }
    case "SET_PD_STAGES": {
      // Kompletten Stufen-Satz fuer eine Pid setzen (z.B. "alle an" / "alle aus").
      // Leere Liste -> Pid faellt raus (siehe TOGGLE_PD_STAGE).
      const { pid, stages } = action.payload || {};
      if (!pid) return state;
      const bereinigt = Array.from(new Set((Array.isArray(stages) ? stages : []).filter(Boolean)));
      if (bereinigt.length === 0) {
        const selNext = (state.pdSelectedPids || []).filter((p) => p !== pid);
        const stagesNext = { ...(state.pdStagesByPid || {}) };
        delete stagesNext[pid];
        return { ...state, pdSelectedPids: selNext, pdStagesByPid: stagesNext, pdReplicate: selNext.length > 0 };
      }
      return {
        ...state,
        pdStagesByPid: { ...(state.pdStagesByPid || {}), [pid]: bereinigt },
      };
    }
    case "SET_RELEASE_STATE":
      return {
        ...state,
        releaseState: action.payload.state,
        releaseError: action.payload.error ?? state.releaseError,
      };
    case "SET_RELEASE_CANDIDATE":
      return { ...state, releaseCandidate: action.payload };
    case "SET_FINALIZED":
      return { ...state, finalized: !!action.payload };
    case "RESET_ALL":
      return { ...initialState, scheduleCount: state.scheduleCount };
    default:
      return state;
  }
}

function extractWerkeFromInstance(inst) {
  if (!inst || !Array.isArray(inst.werke)) return [];
  return inst.werke
    .map((w) => {
      if (typeof w === "string") {
        const parts = w.split("__");
        if (parts.length >= 2) return { key: parts[1] || parts[0], name: parts[0], kennung: parts[1] };
        return { key: parts[0], name: parts[0], kennung: parts[0] };
      }
      const key = w?.kennung || w?.name || "";
      return { key, name: w?.name || key, kennung: w?.kennung || key };
    })
    .filter((w) => w.kennung);
}

const Ctx = createContext(null);

export function TransportV2Provider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTransportV2() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTransportV2 muss innerhalb TransportV2Provider gerufen werden");
  return ctx;
}

// Selektoren
export function hasWerke(state) {
  return !!(state.space?.werkeList?.length);
}
export function allWerke(state) {
  return state.space?.werkeList || [];
}
// Schritt 1 (Landschaften) hat nur mit gepflegten Werken einen eigenen Inhalt (Werke zuschalten);
// ohne Werke zeigt er nur, was die Space-Karte in Schritt 0 schon sagt.
export function landschaftsSchrittNoetig(state) {
  return allWerke(state).length > 0;
}
export function activeWerke(state) {
  return allWerke(state).filter((w) => state.werkOn[w.key]);
}
export function buckets(state) {
  return hasWerke(state) ? activeWerke(state) : [{ key: "std", name: "Standard", kennung: "" }];
}

export function autoDeploy(state, sourceId, werkKey) {
  return !!state.sel?.[sourceId]?.[werkKey] && state.deploy?.[sourceId]?.[werkKey] !== false;
}
export function deploymentSelection(state) {
  const artifacts = {};
  for (const [id, plants] of Object.entries(state.sel || {})) for (const b of buckets(state)) {
    const selectionKey = plants[b.key] ? b.key : b.kennung;
    if (selectionKey && plants[selectionKey]) {
      if (!artifacts[id]) artifacts[id] = {};
      artifacts[id][b.kennung || b.key] = autoDeploy(state, id, selectionKey);
    }
  }
  return { artifacts };
}
export function referenceDeployRequired(state, ref) {
  const consumers = refNutzer(ref);
  return Object.entries(deploymentSelection(state).artifacts).some(([id, plants]) => (!consumers.length || consumers.includes(id)) && Object.values(plants).some(Boolean));
}
export function referenceAutoDeploy(state, ref) {
  return referenceDeployRequired(state, ref) || state.refDeploy?.[refKey(ref)] === true;
}
export function visBuckets(state) {
  const b = buckets(state);
  if (hasWerke(state) && state.workingTab !== "all") {
    return b.filter((x) => x.key === state.workingTab);
  }
  return b;
}

// Vorwaerts-Stufen aus Scene0: alle Stufen ab der Zielstufe (inklusiv), nur die mit
// erreichbarer Instanz. Beispiel: Space DEV/QAS/PRE-PROD/PROD, Zielstufe QAS -> [QAS, PRE-PROD, PROD].
// Ein-Stufen-Space (nur Quelle + Ziel) -> [QAS] (Rueckwaerts-kompatibel zur Ein-Stufen-Ansicht).
// Jede Stufe: { key, code, name, instanceId }.
export function vorwaertsStufen(state) {
  const s = state.space;
  if (!s || !Array.isArray(s.stages)) return [];
  const zielKey = s.zielStufe || null;
  if (!zielKey) return [];
  const startIdx = s.stages.findIndex((st) => st.stageKey === zielKey);
  if (startIdx < 0) return [];
  const out = [];
  for (let i = startIdx; i < s.stages.length; i++) {
    const st = s.stages[i];
    if (!st || !st.instanceId) continue;
    out.push({ key: st.stageKey, code: st.code, name: st.inst || st.name, instanceId: st.instanceId });
  }
  return out;
}

// Selektor: Effektive PD-Pids je Ziel-Stufe. Fehlt der Filter fuer eine Pid,
// gilt (wie im Backend-Export) implizit "alle Stufen". Damit ohne bewusste
// Einschraenkung genau das Altverhalten laeuft. targetStage: normalisierter
// Stufen-Key ("qas"/"preprod"/"prod"/"dev").
export function pdPidsFuerStufe(state, targetStage) {
  const list = Array.isArray(state.pdSelectedPids) ? state.pdSelectedPids : [];
  if (!targetStage) return list.slice();
  const map = state.pdStagesByPid || {};
  return list.filter((pid) => {
    const stg = map[pid];
    if (!Array.isArray(stg) || stg.length === 0) return true; // implizit alle Stufen
    return stg.includes(targetStage);
  });
}
